import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Book1, Calendar, DocumentText1, ArrowUp2, ArrowDown2 } from "iconsax-react";
import cn from "classnames";
import { EmptyState } from "@/shared/components";
import { FileViewer } from "@/shared/ui";
import type { EducationRecord } from "../../types/personalSheet";
import ProfileDataRow from "../ProfileDataRow";

interface Props {
  records: EducationRecord[];
  hasNoEducation?: boolean;
}

export default function EducationSection({ records, hasNoEducation }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  if (records.length === 0) {
    if (hasNoEducation) {
      return <p className="text-body-regular-md content-base-secondary">{t("personalSheet.noEducationDeclared")}</p>;
    }
    return <EmptyState noDataText={t("personalSheet.empty.education")} noDataHint={t("personalSheet.empty.educationHint")} compact />;
  }

  function toggleExpand(id: number) {
    setExpandedIds(function updateSet(prev) {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-3">
      {records.map(function renderRecord(record) {
        const isExpanded = expandedIds.has(record.id);
        const ArrowIcon = isExpanded ? ArrowUp2 : ArrowDown2;

        return (
          <div key={record.id} className="border surface-base-stroke radius-md overflow-hidden">
            <button
              type="button"
              onClick={() => toggleExpand(record.id)}
              className={cn("w-full px-4 py-3 flex items-center justify-between cursor-pointer", isExpanded && "pb-4 border-b surface-base-stroke")}>
              <span className="text-body-bold-md content-base-primary flex-1 min-w-0 text-left truncate">
                {record.degree
                  ? `${t(`personalSheet.degreeValues.${record.degree}`, { defaultValue: record.degree })} | ${record.university_name}`
                  : record.university_name}
              </span>
              <ArrowIcon size={16} color="currentColor" className="shrink-0 ml-3" />
            </button>
            {isExpanded && (
              <div className="px-4 pt-4 pb-4 flex flex-col gap-4">
                <div className="flex flex-col">
                  <ProfileDataRow icon={Book1} label={t("personalSheet.fields.degree")} value={record.degree ? t(`personalSheet.degreeValues.${record.degree}`) : null} />
                  <ProfileDataRow icon={Book1} label={t("personalSheet.fields.universityName")} value={record.university_name} />
                  <ProfileDataRow icon={Book1} label={t("personalSheet.fields.specialty")} value={record.specialty} />
                  <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.admissionYear")} value={record.admission_year} />
                  {record.is_incomplete ? (
                    <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.leftFromCourse")} value={record.left_from_course || null} />
                  ) : (
                    <>
                      <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.graduationYear")} value={record.graduation_year} />
                      <ProfileDataRow icon={DocumentText1} label={t("personalSheet.fields.diplomaNumber")} value={record.diploma_number} />
                    </>
                  )}
                </div>
                {record.diploma_file_url && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.diplomaFile")}</span>
                    <FileViewer existingFileUrl={record.diploma_file_url} isDownloadable />
                  </div>
                )}
                {record.diploma_transcript_file_url && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.diplomaTranscriptFile")}</span>
                    <FileViewer existingFileUrl={record.diploma_transcript_file_url} isDownloadable />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
